import React from 'react';

export type ImportModalSetupStep = 1 | 2 | 3 | 4 | 5 | 6;

export type ImportModalSetupSection = { id: string; text: string };

type GuidedProjectCreationProgressEvent = {
  stage: string;
  status: string;
  detail: string;
  results?: { sections?: ImportModalSetupSection[] };
};

/**
 * Maps the LangGraph node names from
 * `server/graph/guided-project-creation` onto this component's five
 * user-facing steps. `pre_process` and `audition-tools` don't have their
 * own row, so they ride along with the step before/alongside them.
 */
const STAGE_STEP: Partial<Record<string, ImportModalSetupStep>> = {
  start: 1,
  text_extraction: 1,
  file_extraction: 1,
  url_extraction: 1,
  pre_process: 2,
  analyze: 3,
  audition: 4,
  'audition-tools': 4,
  project_setup: 5,
};

/**
 * Drives setup progress from the guided-project-creation SSE relay
 * (`/api/guided-project-creation/:projectId/events`) — a fresh
 * `EventSource` per `active` projectId, closed on cleanup. Stays at step 1
 * and does nothing while `active` is false (Storybook controls `step`
 * directly instead).
 */
export function useGuidedProjectCreationProgress({
  projectId,
  active,
  onFail,
}: {
  projectId: string;
  active: boolean;
  onFail?: (reason: string) => void;
}): { step: ImportModalSetupStep; sections: ImportModalSetupSection[] } {
  const [step, setStep] = React.useState<ImportModalSetupStep>(1);
  const [sections, setSections] = React.useState<ImportModalSetupSection[]>([]);
  const onFailRef = React.useRef(onFail);
  onFailRef.current = onFail;

  React.useEffect(() => {
    if (!active) {
      setStep(1);
      setSections([]);
      return undefined;
    }

    let done = false;
    const source = new EventSource(
      `/api/guided-project-creation/${projectId}/events`
    );

    source.addEventListener('progress', event => {
      let payload: GuidedProjectCreationProgressEvent;
      try {
        payload = JSON.parse((event as MessageEvent).data);
      } catch {
        return;
      }
      const { stage, status, detail, results } = payload;

      if (stage === 'project_setup' && status === 'completed') {
        done = true;
        source.close();
        setSections(results?.sections ?? []);
        setStep(6);
        return;
      }
      if (status === 'failed' || status === 'error') {
        done = true;
        source.close();
        onFailRef.current?.(detail);
        return;
      }
      // Fallback in case `project_setup`'s own `completed` event was
      // somehow missed — the controller always emits this right after.
      if (stage === 'end' && status === 'success' && !done) {
        done = true;
        source.close();
        setStep(6);
        return;
      }

      const next = STAGE_STEP[stage];
      if (next) setStep(prev => (next > prev ? next : prev));
    });

    source.onerror = () => {
      if (!done) {
        source.close();
        onFailRef.current?.('Lost connection while setting up your project.');
      }
    };

    return () => {
      source.close();
    };
  }, [active, projectId]);

  return { step, sections };
}
